import { useState } from 'react';
import {
  faInfo,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createDomicilioText } from 'shared/models/DomicilioGoogle';

type Props = {
  data: any;
  index: number;
  isMobileOrTablet: boolean;
  onLocationsDetails: (reservation: any) => void;
  showInfo?: boolean;
};

type LocationMarkerProps = {
  variant: 'origin' | 'stop' | 'destination';
  stepNumber?: number;
};

const LocationMarker = ({ variant, stepNumber }: LocationMarkerProps) => {
  if (variant === 'origin') {
    return (
      <div className="relative flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full bg-black">
        <div className="h-1.5 w-1.5 rounded-full bg-white" />
      </div>
    );
  }

  if (variant === 'destination') {
    return (
      <div className="flex flex-col bg-whiteLight dark:bg-darkLight h-full">
        <div className="relative flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-sm bg-black">
          <div className="h-1.5 w-1.5 rounded-sm bg-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-black text-[9px] font-bold text-white">
      {stepNumber}
    </div>
  );
};

const LocationRow = ({
  location,
  variant,
  stepNumber
}: {
  location: any;
  variant: LocationMarkerProps['variant'];
  stepNumber?: number;
}) => (
  <div className="relative flex items-start gap-2.5">
    <div className="z-2 relative flex items-center pt-0.5 h-full">
      <LocationMarker variant={variant} stepNumber={stepNumber} />
    </div>
    <p className="text-left text-xs leading-tight text-gray-700 dark:text-ink-dark">
      {createDomicilioText(location.address)}
    </p>
  </div>
);

const Points = ({
  data,
  index,
  isMobileOrTablet,
  onLocationsDetails,
  showInfo = true
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const locations = data[index].locations || [];
  const firstLocation = locations[0];
  const lastLocation = locations[locations.length - 1];
  const middleLocations = locations.slice(1, -1);

  return (
    <div className="flex flex-col text-sm items-start w-full">
      {isMobileOrTablet ? (
        <p className="mb-1 font-semibold">Domicilios</p>
      ) : null}

      <div className="relative flex flex-col space-y-3">
        {/* Línea vertical negra fija conectora */}
        {locations.length > 1 && (
          <div
            style={{
              position: 'absolute',
              bottom: '36px',
              left: '6px',
              top: '20px',
              zIndex: 0,
              width: '2px',
              backgroundColor: 'black'
            }}
          />
        )}

        {/* Origen */}
        {firstLocation ? (
          <LocationRow location={firstLocation} variant="origin" />
        ) : null}

        {/* Botón expansor para puntos intermedios */}
        {middleLocations.length > 0 ? (
          <div
            className="flex cursor-pointer items-center space-x-1 text-xs text-inkMuted hover:text-black dark:text-inkMuted-dark dark:hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            style={{ position: 'absolute', left: '-12px', top: '17px' }}
          >
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
          </div>
        ) : null}

        {/* Puntos intermedios enumerados a partir del 2 */}
        {isExpanded
          ? middleLocations.map((location, idx) => (
              <LocationRow
                key={location.id ?? createDomicilioText(location.address)}
                location={location}
                variant="stop"
                stepNumber={idx + 2}
              />
            ))
          : null}

        {/* Destino (si hay más de 1 punto) */}
        {locations.length > 1 ? (
          <LocationRow location={lastLocation} variant="destination" />
        ) : null}

        {/* Botón de información adicional */}
        {showInfo ? (
          <div className="flex w-full items-center justify-center pt-1">
            <div
              className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-2 border-blue-300 text-blue-300 hover:border-blue-500 hover:text-blue-500"
              onClick={(e) => {
                e.stopPropagation();
                onLocationsDetails(data[index]);
              }}
            >
              <FontAwesomeIcon icon={faInfo} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Points;
